import React from "react";
import { cn } from "@/lib/utils";
import {
  getMultipleChoiceSelectedCount,
  getMultipleChoiceStatusLabel,
  type MultipleChoiceSelectionState,
} from "./multipleChoiceSelection";

type MultipleChoiceSelectionProgressProps = {
  selectionState: MultipleChoiceSelectionState;
  progressMessage: string;
  showLimitHint: boolean;
};

const statusLabelClass: Record<
  NonNullable<ReturnType<typeof getMultipleChoiceStatusLabel>>["tone"],
  string
> = {
  complete: "font-semibold text-emerald-700",
  almost: "font-medium text-amber-700",
  remaining: "font-medium text-blue-700",
};

export const MultipleChoiceSelectionProgress: React.FC<
  MultipleChoiceSelectionProgressProps
> = ({ selectionState, progressMessage, showLimitHint }) => {
  const statusLabel = getMultipleChoiceStatusLabel(selectionState);
  const selectedCount = getMultipleChoiceSelectedCount(selectionState);
  const requiredCount = selectionState.requiredCount;
  const showSegments =
    selectionState.useExactCount &&
    requiredCount != null &&
    requiredCount >= 2;

  return (
    <div
      className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {showSegments && statusLabel && (
        <>
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Progresso da seleção
            </span>
            <span
              className={cn("text-xs", statusLabelClass[statusLabel.tone])}
            >
              {statusLabel.text}
            </span>
          </div>
          <div
            className="flex h-3 gap-2"
            aria-hidden="true"
            aria-label={`${selectedCount} de ${requiredCount} selecionadas`}
          >
            {Array.from({ length: requiredCount }, (_, index) => (
              <div
                key={index}
                className={cn(
                  "flex-1 rounded-full transition-colors duration-200",
                  index < selectedCount ? "bg-emerald-500" : "bg-slate-200"
                )}
              />
            ))}
          </div>
        </>
      )}
      <p className={cn("text-sm text-slate-700", showSegments && "mt-2")}>
        {progressMessage}
      </p>
      {showLimitHint && (
        <p className="mt-1 text-xs text-slate-500">
          Desmarque uma opção se quiser escolher outra.
        </p>
      )}
    </div>
  );
};
